import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameWeek, Season, TeamAbrvToTeamID } from '@shared/constants';
import { toSeasonWeekID } from '@shared/helpers';
import { GameID, SeasonID, SeasonWeekID, TeamAbrvType } from '@shared/types';
import {
  ActiveStageState,
  AppMonitorState,
  AppThunk,
  GameNavCategory,
  GameNavCategoryType,
  RootState,
  StageState,
} from 'types';

export type GamesProps = {
  [key: string]: any;
  isLoading: boolean;
  weekSelected: GameWeek;
  yearSelected: SeasonID;
  seasonWeekSelected: SeasonWeekID;
  gameSelected: GameID;
  teamSelected: string;
  gameCategorySelected: GameNavCategoryType;
  appMonitor: AppMonitorState;
};

const initialState: GamesProps = {
  // sceneSelected: AppScene.Authentication,
  isLoading: false,
  weekSelected: GameWeek.Week1,
  yearSelected: Season.Season2025,
  seasonWeekSelected: toSeasonWeekID(Season.Season2025, GameWeek.Week1),
  gameSelected: '',
  teamSelected: '',
  gameCategorySelected: GameNavCategory.Game,
  // appInitialized: false,

  appMonitor: {
    appActivated: false,
    latestAvailableWeek: GameWeek.Week1,
    loadingStage: 'NONE',
    stages: {
      byeWeekTeamsLoaded: false,
      gameResultsLoaded: false,
      gameScheduleLoaded: false,
      rankingsLoaded: false,
      teamDetailsLoaded: false,
      teamResultsLoaded: false,
      teamStandingsLoaded: false,
    },
  },
};

export const updateGamesSliceWithProps = (): AppThunk => (dispatch, getState) => {
  if (typeof getState().nfl.teamSelected === 'undefined') {
    dispatch(setSelectedTeam(''));
  }
};

export const onSelectedWeekChanged =
  (newWeek: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const currentWeek = getState().nfl.weekSelected;
    if (newWeek !== currentWeek && newWeek in GameWeek) {
      dispatch(setSelectedWeek(newWeek));
      dispatch(setSelectedSeasonWeek(toSeasonWeekID(getState().nfl.yearSelected, newWeek)));
    }
  };

export const onSelectedYearChanged =
  (newYear: SeasonID): AppThunk =>
  (dispatch, getState) => {
    const currentYear = getState().nfl.yearSelected;
    if (newYear !== currentYear) {
      dispatch(setSelectedYear(newYear));
      const currentWeek = getState().nfl.weekSelected;
      const week = currentWeek in GameWeek ? currentWeek : GameWeek.Week1;
      dispatch(setSelectedSeasonWeek(toSeasonWeekID(newYear, week)));
    }
  };

export const gamesSlice = createSlice({
  name: 'nfl',
  initialState,
  reducers: {
    // setSelectedScene: (state, action: PayloadAction<Scene>) => {
    //   state.sceneSelected = action.payload;
    // },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSelectedWeek: (state, action: PayloadAction<GameWeek>) => {
      state.weekSelected = action.payload;
    },
    setSelectedYear: (state, action: PayloadAction<string>) => {
      state.yearSelected = action.payload;
    },
    setSelectedSeasonWeek: (state, action: PayloadAction<SeasonWeekID>) => {
      state.seasonWeekSelected = action.payload;
    },
    setSelectedGame: (state, action: PayloadAction<GameID>) => {
      state.gameSelected = action.payload;
      if (action.payload !== '') state.gameCategorySelected = GameNavCategory.Game;
    },
    setSelectedTeam: (state, action: PayloadAction<string>) => {
      state.teamSelected = action.payload;
    },
    setSelectedGameCategoryType: (state, action: PayloadAction<GameNavCategoryType>) => {
      state.gameCategorySelected = action.payload;
    },
    // ACTIVATION AND INITIALIZATION
    // setAppInitialized: (state, action: PayloadAction<boolean>) => {
    //   state.appInitialized = action.payload;
    // },
    setAppMonitorState: (state, action: PayloadAction<AppMonitorState>) => {
      state.appMonitor = action.payload;
    },
    setAppMonitorLatestAvailableWeek: (state, action: PayloadAction<GameWeek>) => {
      state.appMonitor.latestAvailableWeek = action.payload;
    },
    setAppMonitorLoadingStage: (state, action: PayloadAction<ActiveStageState>) => {
      state.appMonitor.loadingStage = action.payload;
    },
    setAppMonitorAppActivated: (state, action: PayloadAction<boolean>) => {
      state.appMonitor.appActivated = action.payload;
    },
    setAppMonitorLoadedStages: (state, action: PayloadAction<Partial<StageState>>) => {
      state.appMonitor.stages = { ...state.appMonitor.stages, ...action.payload };
    },
    resetGamesConfig: () => {
      return { ...initialState };
    },
  },
});

export const {
  // setSelectedScene,
  setIsLoading,
  setSelectedGame,
  setSelectedWeek,
  setSelectedYear,
  setSelectedSeasonWeek,
  setSelectedTeam,
  setSelectedGameCategoryType,
  // setAppInitialized,
  setAppMonitorState,
  setAppMonitorLatestAvailableWeek,
  setAppMonitorLoadingStage,
  setAppMonitorAppActivated,
  setAppMonitorLoadedStages,
  resetGamesConfig,
} = gamesSlice.actions;

// #region UI Selectors *********************************************************
// export const getSelectedScene = (state: RootState): Scene => state.nfl.sceneSelected;
export const getSelectedWeek = (state: RootState): GameWeek => state.nfl.weekSelected;
export const getSelectedYear = (state: RootState): SeasonID => state.nfl.yearSelected;
export const getSelectedSeasonWeek = (state: RootState): SeasonWeekID => state.nfl.seasonWeekSelected;
export const getSelectedGame = (state: RootState): GameID => state.nfl.gameSelected;
export const getSelectedTeamName = (state: RootState): string => state.nfl.teamSelected;
export const getSelectedGameCategoryType = (state: RootState): GameNavCategoryType => state.nfl.gameCategorySelected;
export const getSelectedTeamID = (state: RootState): string => TeamAbrvToTeamID[state.nfl.teamSelected as TeamAbrvType];
// export const getIsAppInitialized = (state: RootState): boolean => !!state.nfl.appInitialized;
export const getIsLoading = (state: RootState): boolean => state.nfl.isLoading;
// export const getIsAlreadyInitialized = memoize(
//   (state: RootState): boolean => getIsAppInitialized(state) && !getIsLoading(state) && getAppActivated(state)
// );

// #endregion

// #region APP MONITOR ********************************************************
export const getAppMonitorState = (state: RootState): AppMonitorState => state.nfl.appMonitor;
export const getLatestAvailableWeek = (state: RootState): GameWeek => state.nfl.appMonitor.latestAvailableWeek;
export const getAppMonitorLoadingStage = (state: RootState): ActiveStageState => state.nfl.appMonitor.loadingStage;
export const getAppActivated = (state: RootState): boolean => state.nfl.appMonitor.appActivated;
export const getAppMonitorLoadedStages = (state: RootState): StageState => state.nfl.appMonitor.stages;
// #endregion

export default gamesSlice.reducer;
